import type { Dosage } from '@medplum/fhirtypes';
import type { FhirTimingRepeat } from './fhirTiming';
import { parseSigDosage } from './sigDosage';
import { inferDosesPerDayFromSig, isAsNeededSig } from './sigParsing';

/** The three dose fields a medication form opens with, each `undefined` when nothing states it. */
export type DosageFieldSeed = { dose: number | undefined; units: string | undefined; route: string | undefined };

const conceptDisplay = (concept: Dosage['route']): string | undefined => concept?.text ?? concept?.coding?.[0]?.display;

/**
 * Seed a medication form's dose, units and route from a record's primary dosage: the structured
 * fields when the record has them, else read off the sig text.
 *
 * A prescription synced from ScriptSure stores its sig as `text` alone — no `doseAndRate`, `method`,
 * `route` or `timing` (Liza-Health/medplum-tracker#27) — so without the fallback its edit form opens
 * empty beside directions that plainly say "Take 1 tablet by mouth once daily". The sig on such a
 * record is either the sentence Liza composed for the pending order or the vendor's own template
 * grammar, both of which {@link parseSigDosage} was written for; it maps onto the form's closed
 * vocabularies and yields `undefined` for anything it cannot read unambiguously, so a field is left
 * empty rather than guessed. The structured fields, when present, always win: they are what the
 * prescriber entered, not a reading of prose.
 */
export function seedDosageFields(dosage: Dosage | undefined): DosageFieldSeed {
  const structured: DosageFieldSeed = {
    dose: dosage?.doseAndRate?.[0]?.doseQuantity?.value,
    units: conceptDisplay(dosage?.method),
    route: conceptDisplay(dosage?.route),
  };
  if (structured.dose !== undefined || structured.units || structured.route) return structured;
  return parseSigDosage(dosage?.text);
}

export type ScheduleSeed = {
  /** A fixed daily or every-N-days schedule the sig states, in the form's timing shape. */
  timing?: Pick<FhirTimingRepeat, 'frequency' | 'period' | 'periodUnit'>;
  /** The sig marks the medication as-needed (PRN). */
  asNeeded: boolean;
};

/** Doses a day above which a sig seeds no timing. Every shape here is a rate per day, and past six
 * the rate stops reading as one ("12 times daily" for q2h), so the sig is left to the picker's
 * default. */
const MAX_DAILY_FREQUENCY = 6;

/**
 * Seed a schedule from a sig line, for a record whose Task carries none: a whole number of doses per
 * day becomes `frequency` per one day; an every-N-days interval becomes one dose per N days; a PRN
 * marker sets `asNeeded`. Anything else — an hourly interval that does not divide the day, an
 * interval shorter than four hours, a time-of-day the picker cannot express as a rate, no frequency
 * at all — seeds no timing, so the picker opens on its default rather than on a guess.
 *
 * The two axes are read independently, so "every 6 hours as needed" seeds both the PRN flag and the
 * 4×/day ceiling it states rather than only the flag.
 *
 * Every seed is a rate per day, so an hourly sig is re-rendered as that rate ("every 6 hours as
 * needed" reads back as "4 times daily as needed") and q2h/q3h exceed {@link MAX_DAILY_FREQUENCY}
 * and seed the flag alone. The picker itself holds hourly intervals (1, 2, 4, 6, 8, 12 hours), so
 * both are limits of this reading, not of what an item can carry.
 */
export function seedScheduleFromSig(sigLine: string | undefined): ScheduleSeed {
  if (!sigLine?.trim()) return { asNeeded: false };
  const asNeeded = isAsNeededSig(sigLine);

  const dosesPerDay = inferDosesPerDayFromSig(sigLine);
  if (!dosesPerDay) return { asNeeded };

  if (Number.isInteger(dosesPerDay) && dosesPerDay >= 1 && dosesPerDay <= MAX_DAILY_FREQUENCY) {
    return { asNeeded, timing: { frequency: dosesPerDay, period: 1, periodUnit: 'd' } };
  }
  const everyDays = 1 / dosesPerDay;
  if (dosesPerDay < 1 && Number.isInteger(Number(everyDays.toFixed(6)))) {
    return { asNeeded, timing: { frequency: 1, period: Math.round(everyDays), periodUnit: 'd' } };
  }
  return { asNeeded };
}
